<template>
  <v-container fluid>
    <v-row>
      <!-- Jira Host URL -->
      <v-col cols="12" md="4" lg="3">
        <v-card
          height="100%"
        >
          <v-card-title>
            Jira Host URL
            <v-spacer></v-spacer>
            <v-btn
              small
              color="primary"
              @click="showJiraAccessTokenDialog = true"
              v-if="!hasAccess"
            >
              Generate Access Token
            </v-btn>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-text-field
              v-model="jiraUrl"
              :disabled="!hasAccess"
              dense
              hint="Example: https://jira-hostname.atlassian.net"
              label="Jira URL"
            ></v-text-field>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              :disabled="!jiraUrl"
              color="primary"
              @click="getProjects"
            >
              Get Projects
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
      <!-- /Jira Host URL -->
      <!-- Jira Projects Selector -->
      <v-col cols="12" md="4" lg="3">
        <v-card
          height="100%"
        >
          <v-card-title>
            Jira Project
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-autocomplete
              dense
              :disabled="!projects.length"
              :items="projects"
              v-model="selectedProject"
              @change="getBoards"
              item-text="name"
              item-value="key"
            >
              <template v-slot:item="data">
                <v-list-item-avatar>
                  <img :src="data.item.icon">
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>
                    {{ data.item.name }}({{ data.item.key }})
                  </v-list-item-title>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              :disabled="!selectedProject"
              color="primary"
              @click="getBoards"
            >
              Get Boards
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
      <!-- /Jira Projects Selector -->
      <!-- Jira Boards Selector -->
      <v-col cols="12" md="4" lg="3">
        <v-card
          height="100%"
        >
          <v-card-title>
            Jira Boards
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-autocomplete
              dense
              :disabled="!boards.length"
              :items="boards"
              v-model="selectedBoard"
              @change="getSprints"
              item-text="name"
              item-value="id"
            >
              <template v-slot:item="data">
                <v-list-item-content>
                  <v-list-item-title>
                    {{ data.item.name }}({{ data.item.type }})
                  </v-list-item-title>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              :disabled="!selectedBoard"
              color="primary"
              @click="getSprints"
            >
              Get Sprints
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
      <!-- /Jira Boards Selector -->
      <!-- Jira Sprint Selector -->
      <v-col cols="12" md="4" lg="3">
        <v-card
          height="100%"
        >
          <v-card-title>
            Jira Sprints
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-autocomplete
              dense
              :disabled="!boards.length"
              :items="sprints"
              v-model="selectedSprint"
              item-text="name"
              item-value="id"
              @change="getVelocity"
            >
              <template v-slot:item="data">
                <v-list-item-content>
                    <v-list-item-title>
                      {{data.item.name}}({{ data.item | sprintState }})
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      {{ data.item.startDate | dateFormat }} - {{ data.item.endDate | dateFormat }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
              </template>
            </v-autocomplete>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              :disabled="!selectedSprint"
              color="primary"
              @click="getVelocity"
            >
              Get Velocity
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
      <!-- /Jira Sprint Selector -->
    </v-row>
    <v-row>
      <!-- Velocity -->
      <v-col cols="12" md="4" lg="3">
        <v-card>
          <v-card-title>
            Velocity
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <template v-if="velocity">
              Sum of <b>All</b> Story points: <b>{{ velocity.totalAll }}</b><br>
              Sum of <b>Done</b> Story points: <b>{{ velocity.totalDone }}</b>
            </template>
            <template v-else>
              Choose Sprint to get Velocity
            </template>
          </v-card-text>
        </v-card>
      </v-col>
      <!-- /Velocity -->
      <!-- Counts -->
      <v-col cols="12" md="4" lg="3">
        <v-card>
          <v-card-title>
            Counts by issue types
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <template v-if="velocity">
              <div v-for="(count, key) in velocity.totalByTypes" :key="key">
                <b>{{ count.type }}</b> - {{ count.count }}
              </div>
            </template>
            <template v-else>
              Choose Sprint to get Velocity
            </template>
          </v-card-text>
        </v-card>
      </v-col>
      <!-- /Counts -->
      <!-- Blocked/On Hold/Hold/Flagged -->
      <v-col cols="12" md="4" lg="3">
        <v-card>
          <v-card-title>
            Counts by issue status
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <template v-if="velocity">
              <v-row>
                <v-col>
                  <div>
                    <b>Blocked</b> - {{ velocity.totalByStatuses.blocked }}
                  </div>
                  <div>
                    <b>On Hold</b> - {{ velocity.totalByStatuses.onhold }}
                  </div>
                </v-col>
                <v-col>
                  <div>
                    <b>Flagged</b> - {{ velocity.totalByStatuses.flagged }}
                  </div>
                </v-col>
              </v-row>
            </template>
            <template v-else>
              Choose Sprint to get Velocity
            </template>
          </v-card-text>
        </v-card>
      </v-col>
      <!-- /Blocked/On Hold/Hold/Flagged -->
    </v-row>
    <JiraApiTokenDialog
      v-model="showJiraAccessTokenDialog"
      @successSaved="hasAccess = true"
    />
  </v-container>
</template>
<script>
export default {
  name: 'JiraVelocity',
  components: {
    JiraApiTokenDialog: () => import('@/components/global/JiraApiTokenDialog')
  },
  data () {
    return {
      hasAccess: true,
      showJiraAccessTokenDialog: false,
      jiraUrl: 'https://devcomprojects.atlassian.net/',
      projects: [],
      boards: [],
      sprints: [],
      velocity: null,
      selectedProject: null,
      selectedBoard: null,
      selectedSprint: null,
      storyPointFieldId: null,
      countsOfTasks: null
    }
  },
  computed: {
    user () {
      return this.$store.getters.getUser
    }

  },
  filters: {
    dateFormat (d) {
      return new Date(d).toISOString().substring(0, 10)
    },
    sprintState (s) {
      return s.state === 'closed' ? `${s.state} on ${new Date(s.completeDate).toISOString().substring(0, 10)}` : s.state
    }
  },
  mounted () {
    this.checkIfHasAccessToken()
  },
  methods: {
    checkIfHasAccessToken () {
      this.axios.get('/api/check-jira-api-access', {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.hasAccess = res.data[0].Has_Access
        this.showJiraAccessTokenDialog = !this.hasAccess
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    getProjects () {
      this.boards = []
      this.sprints = []
      this.velocity = null
      this.selectedBoard = null
      this.selectedSprint = null
      this.storyPointFieldId = null
      this.axios.post('/api/jira-velocity/get-projects', {
        url: this.jiraUrl
      },
      {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.projects = res.data.values
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    getBoards () {
      this.sprints = []
      this.velocity = null
      this.selectedSprint = null
      this.axios.post('/api/jira-velocity/get-boards', {
        url: this.jiraUrl,
        projectKey: this.selectedProject
      },
      {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.boards = res.data.values
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    getSprints () {
      this.velocity = null
      this.axios.post('/api/jira-velocity/get-sprints', {
        url: this.jiraUrl,
        boardId: this.selectedBoard
      },
      {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.sprints = res.data.values
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    async getStoryPointFieldId () {
      return this.axios.post('/api/jira-velocity/get-fields', {
        url: this.jiraUrl
      },
      {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.storyPointFieldId = res.data.id
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    async getVelocity () {
      if (!this.storyPointFieldId) {
        await this.getStoryPointFieldId()
      }
      this.axios.post('/api/jira-velocity/get-issues', {
        url: this.jiraUrl,
        sprintId: this.selectedSprint,
        fieldId: this.storyPointFieldId
      },
      {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.velocity = res.data
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    }
  }
}
</script>
<style lang="scss" scoped>
   .sticky-col, .v-item--active {
     position: sticky;
     top: 0;
     z-index: 1;
   }
   .v-item--active {
     background-color: #fff;
   }
</style>
